import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    FileText,
    Download,
    Printer,
    ArrowLeft,
    Building2,
    Calendar,
    DollarSign,
    User,
    Phone,
    Mail,
    CheckCircle,
    XCircle,
    Clock,
    Hotel,
    Plane,
    Car,
    Activity,
    Package,
    File,
    ChevronDown,
    ChevronUp,
    Share2,
    AlertCircle
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import ReusableTable from '../../components/Tableau';
import InvoiceExport from './InvoiceExport';

const InvoiceDetailsProvider = ({ lang }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(lang);
    const [showExportModal, setShowExportModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [invoice, setInvoice] = useState(null);
    const [expandedSections, setExpandedSections] = useState({
        issuer: true,
        customer: true,
        bookings: true
    });

    // Charger les détails de la facture
    useEffect(() => {
        fetchInvoiceDetails();
    }, [id]);

    const fetchInvoiceDetails = async () => {
        try {
            setLoading(true);
            const invoice_url = process.env.REACT_APP_API_INVOICE_URL;
            const response = await api.get(`${invoice_url}/provider/${id}`);

            if (response.data && response.data.result) {
                setInvoice(response.data.result);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des détails:', error);
            toast.error(t('errorLoadingInvoiceDetails'));
        } finally {
            setLoading(false);
        }
    };

    // Toggle des sections
    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Formater la date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const [day, month, year] = dateString.split('/');
            return new Date(`${year}-${month}-${day}`).toLocaleDateString(lang);
        } catch {
            return dateString;
        }
    };

    // Obtenir l'icône du service
    const getServiceIcon = (service) => {
        switch (service?.toLowerCase()) {
            case 'hotel': return <Hotel size={16} color="#7c3aed" />;
            case 'flight': return <Plane size={16} color="#3b82f6" />;
            case 'car': return <Car size={16} color="#10b981" />;
            case 'activity': return <Activity size={16} color="#f59e0b" />;
            case 'package': return <Package size={16} color="#8b5cf6" />;
            default: return <File size={16} color="#6b7280" />;
        }
    };

    // Colonnes pour le tableau des réservations
    const bookingColumns = [
        {
            label: t('bookingNumber'),
            key: 'booking_number',
        },
        {
            label: t('service'),
            key: 'service_name',
            sortable: true,

            render: (value, row) => (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    width: 'min-content',
                }}>
                    <span style={{ fontWeight: '500' }}>{value || row.product_name}</span>
                    <span style={{
                        fontSize: '12px',
                        color: '#6f728f',
                        background: '#7c3aed2f',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        width: 'min-content',
                    }}>
                        {row.service}
                    </span>
                </div>
            )
        },
        {
            label: t('holder'),
            key: 'holder',
        },
        {
            label: t('dates'),
            key: 'checkin',
            sortable: true,
            render: (value, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{t('checkin')}</span>
                    <span style={{ fontWeight: '500' }}>{formatDate(value)}</span>
                    <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{t('checkout')}</span>
                    <span style={{ fontWeight: '500' }}>{formatDate(row.checkout)}</span>
                </div>
            )
        },
        {
            label: t('amount'),
            key: 'amount',
            sortable: true,
            render: (value, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', }}>
                    <div >
                        <span style={{ fontWeight: '600', fontSize: '16px' }}>
                            {parseFloat(value).toFixed(2)}
                        </span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>{row.currency}</span>
                </div>
            )
        },
        {
            label: t('status'),
            key: 'status',
            sortable: true,
            render: (value) => (
                <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: value?.toLowerCase() === 'confirmed' ? '#065f46' :
                        value?.toLowerCase() === 'cancelled' ? '#991b1b' : '#92400e',
                    background: value?.toLowerCase() === 'confirmed' ? '#d1fae5' :
                        value?.toLowerCase() === 'cancelled' ? '#fee2e2' : '#fef3c7',
                    textTransform: 'capitalize',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    {value?.toLowerCase() === 'confirmed' ? <CheckCircle size={12} /> :
                        value?.toLowerCase() === 'cancelled' ? <XCircle size={12} /> :
                            <Clock size={12} />}
                    {value}
                </span>
            )
        },
        {
            label: t('createdAt'),
            key: 'created_at',
            sortable: true,
            render: (value) => (
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    {new Date(value).toLocaleString(lang)}
                </div>
            )
        }
    ];

    // Fonction pour exporter les réservations en Excel (à ajouter dans Invoices.js)
const exportBookingsToExcel = (bookings, invoiceType = 'provider') => {
    
    if (!bookings || bookings.length === 0) {
        toast.error(t('noDataToExport'));
        return;
    }

    try {
        // Calculer les statistiques
        const stats = {
            total: bookings.length,
            totalAmount: bookings.reduce((sum, booking) => sum + parseFloat(booking.amount || 0), 0),
            byService: {},
            byStatus: {},
            byCurrency: {}
        };

        // Préparer les données pour Excel
        const excelData = bookings.map(booking => {
            const service = booking.service || 'unknown';
            const status = booking.status || 'unknown';
            const currency = booking.currency || 'USD';
            
            // Compter les statistiques
            stats.byService[service] = (stats.byService[service] || 0) + 1;
            stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
            stats.byCurrency[currency] = (stats.byCurrency[currency] || 0) + parseFloat(booking.amount || 0);
            
            return {
                [t('bookingId')]: booking.booking_number,
                [t('rateId')]: booking.rate_id,
                [t('bookingNumber')]: booking.booking_number,
                [t('service')]: booking.service,
                [t('serviceName')]: booking.service_name || '-',
                [t('productName')]: booking.product_name || '-',
                [t('holder')]: booking.holder,
                [t('checkin')]: booking.checkin,
                [t('checkout')]: booking.checkout,
                [t('status')]: booking.status,
                [t('amount')]: parseFloat(booking.amount || 0).toFixed(2),
                [t('currency')]: booking.currency,
                [t('createdAt')]: booking.created_at ? new Date(booking.created_at).toLocaleString(lang) : '-',
                [t('invoiceType')]: invoiceType === 'customer' ? t('customerInvoice') : t('providerInvoice')
            };
        });

        // Créer la feuille de données
        const wsData = XLSX.utils.json_to_sheet(excelData);


        // Créer le classeur
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsData, t('bookings'));

        // Mettre en forme les colonnes
        if (wsData['!ref']) {
            const range = XLSX.utils.decode_range(wsData['!ref']);
            const wscols = [];
            for (let C = range.s.c; C <= range.e.c; ++C) {
                let maxLength = 0;
                for (let R = range.s.r; R <= range.e.r; ++R) {
                    const cell = wsData[XLSX.utils.encode_cell({ c: C, r: R })];
                    if (cell && cell.v) {
                        const cellLength = cell.v.toString().length;
                        if (cellLength > maxLength) maxLength = cellLength;
                    }
                }
                wscols.push({ wch: Math.min(maxLength + 2, 30) });
            }
            wsData['!cols'] = wscols;
        }

        // Générer le nom du fichier
        const date = new Date();
        const dateStr = date.toISOString().split('T')[0];
        const fileName = `bookings_${invoiceType}_${dateStr}.xlsx`;

        // Télécharger
        XLSX.writeFile(wb, fileName);
        toast.success(t('exportSuccess'));

    } catch (error) {
        console.error('Erreur lors de l\'export des réservations:', error);
        toast.error(t('exportError'));
    }
};

    // Styles améliorés
    const styles = {
        container: {
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
            minHeight: '100vh'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
            padding: '20px 0',
            borderBottom: '1px solid #e5e7eb'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
        },
        backButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: '#4b5563',
            transition: 'all 0.2s ease',
            marginBottom: '8px',
            width: 'fit-content',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
        },
        pageTitle: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        headerActions: {
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
        },
        actionGroup: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
        },
        actionSeparator: {
            width: '1px',
            height: '24px',
            background: '#e5e7eb'
        },
        iconButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: 'white',
            color: '#4b5563',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f9fafb',
                borderColor: '#7c3aed',
                color: '#7c3aed'
            }
        },
        exportGroup: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
        },
        primaryButton: {
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            fontSize: '14px',
            boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)',

        },
        secondaryButton: {
            padding: '10px 18px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            fontSize: '14px',
            transition: 'all 0.2s ease',
        },
        // Styles pour la carte de facture
        invoiceMeta: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '16px'
        },
        invoiceTypeBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: '#e0e7ff',
            color: '#7c3aed',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '500'
        },
        invoiceBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            background: '#f3f4f6',
            color: '#4b5563',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500'
        },
        invoiceNumber: {
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#1f2937',
            letterSpacing: '-0.5px',
            marginBottom: '12px'
        },
        invoiceDates: {
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        dateItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            color: '#6b7280'
        },
        amountStatus: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '12px',
            fontSize: '14px'
        },
        invoiceCard: {
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb'
        },
        invoiceHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e5e7eb'
        },
        invoiceInfo: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
        },
        invoiceType: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#6b7280'
        },
        amountCard: {
            background: '#f5f3ff',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            minWidth: '220px',
            marginBottom: '8px'
        },
        amountLabel: {
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        amountValue: {
            fontSize: '40px',
            fontWeight: 'bold',
            color: '#1f2937',
            lineHeight: '1'
        },
        amountCurrency: {
            fontSize: '20px',
            color: '#7c3aed',
            marginLeft: '4px'
        },
        // Détails de la facture
        detailsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
        },
        detailCard: {
            background: '#f5f3ff',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #e5e7eb',
            transition: 'all 0.2s ease',

        },
        detailTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e5e7eb'
        },
        detailGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '16px'
        },
        detailItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px'
        },
        detailIcon: {
            color: '#7c3aed',
            flexShrink: 0,
            marginTop: '2px'
        },
        detailContent: {
            flex: 1
        },
        detailLabel: {
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        detailValue: {
            fontSize: '15px',
            color: '#1f2937',
            fontWeight: '500'
        },
        // Sections expansibles
        sectionCard: {
            background: 'white',
            borderRadius: '16px',
            marginBottom: '24px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb'
        },
        sectionHeader: {
            padding: '20px 24px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f3f4f6'
            }
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        sectionBadge: {
            fontSize: '12px',
            padding: '2px 8px',
            background: '#e0e7ff',
            color: '#7c3aed',
            borderRadius: '12px',
            fontWeight: '500'
        },
        sectionToggle: {
            color: '#6b7280',
            transition: 'transform 0.2s ease'
        },
        sectionContent: {
            padding: '8px'
        },
        // Adresse
        addressCard: {
            margin: '8px',
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #e5e7eb',
            marginBottom: '20px'
        },
        addressHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        addressIcon: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: '#e0e7ff',
            borderRadius: '10px',
            color: '#7c3aed'
        },
        addressTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0
        },
        addressSubtitle: {
            fontSize: '14px',
            color: '#6b7280',
            marginTop: '2px'
        },
        addressContent: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
        },
        addressLine: {
            marginBottom: '8px',
            color: '#374151',
            fontSize: '14px',
            lineHeight: '1.6'
        },
        contactInfo: {
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb'
        },
        contactItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            color: '#4b5563',
            fontSize: '14px'
        },
        contactLink: {
            color: '#7c3aed',
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline'
            }
        },
        // Tableau des réservations
        bookingsContainer: {
            marginTop: '8px',
            padding: '0px',
        },
        bookingsHeader: {
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
        },
        bookingsStats: {
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
        },
        statBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            background: '#f3f4f6',
            color: '#4b5563',
            borderRadius: '20px',
            fontSize: '13px'
        },
        // Status card
        statusCard: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 20px',
            background: invoice?.status?.toLowerCase() === 'paid' ? '#d1fae5' :
                invoice?.status?.toLowerCase() === 'unpaid' ? '#fef3c7' : '#fee2e2',
            border: `1px solid ${invoice?.status?.toLowerCase() === 'paid' ? '#a7f3d0' :
                invoice?.status?.toLowerCase() === 'unpaid' ? '#fde68a' : '#fecaca'}`,
            borderRadius: '12px',
            marginBottom: '24px'
        },
        statusIcon: {
            color: invoice?.status?.toLowerCase() === 'paid' ? '#065f46' :
                invoice?.status?.toLowerCase() === 'unpaid' ? '#92400e' : '#991b1b'
        },
        statusContent: {
            flex: 1
        },
        statusTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: invoice?.status?.toLowerCase() === 'paid' ? '#065f46' :
                invoice?.status?.toLowerCase() === 'unpaid' ? '#92400e' : '#991b1b',
            marginBottom: '4px'
        },
        statusDescription: {
            fontSize: '14px',
            color: invoice?.status?.toLowerCase() === 'paid' ? '#047857' :
                invoice?.status?.toLowerCase() === 'unpaid' ? '#b45309' : '#b91c1c'
        },
        // Loading state
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh'
        },
        loadingSpinner: {
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #7c3aed',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite'
        },
        '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' }
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div style={styles.loadingSpinner}></div>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <div style={styles.breadcrumb}>
                        <a
                            style={styles.breadcrumbLink}
                            onClick={() => navigate('/invoices')}
                        >
                            <ArrowLeft size={16} />
                            {t('backToInvoices')}
                        </a>
                    </div>
                </div>
                <div style={{ textAlign: 'center', padding: '100px 20px' }}>
                    <FileText size={64} color="#9ca3af" />
                    <p style={{ marginTop: '24px', fontSize: '20px', color: '#1f2937' }}>
                        {t('invoiceNotFound')}
                    </p>
                    <p style={{ marginTop: '8px', color: '#6b7280' }}>
                        {t('invoiceNotFoundDescription')}
                    </p>
                    <button
                        style={{ ...styles.primaryButton, marginTop: '24px' }}
                        onClick={() => navigate('/invoices')}
                    >
                        <ArrowLeft size={16} />
                        {t('backToInvoices')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>

            <button
                style={styles.backButton}
                onClick={() => navigate('/invoices')}
            >
                <ArrowLeft size={18} />
                {t('backToInvoices')}
            </button>

            {/* Carte principale de la facture */}
            <div style={styles.invoiceCard}>
                <div style={styles.statusCard}>
                    {invoice.status?.toLowerCase() === 'paid' ?
                        <CheckCircle size={24} style={styles.statusIcon} /> :
                        invoice.status?.toLowerCase() === 'unpaid' ?
                            <Clock size={24} style={styles.statusIcon} /> :
                            <AlertCircle size={24} style={styles.statusIcon} />
                    }
                    <div style={styles.statusContent}>
                        <div style={styles.statusTitle}>
                            {invoice.status?.toLowerCase() === 'paid' ? t('invoicePaid') :
                                invoice.status?.toLowerCase() === 'unpaid' ? t('invoiceUnpaid') :
                                    t('invoiceOverdue')}
                        </div>
                        <div style={styles.statusDescription}>
                            {invoice.status?.toLowerCase() === 'paid' ?
                                `${t('paidOn')}: ${formatDate(invoice.paid_at)}` :
                                invoice.status?.toLowerCase() === 'unpaid' ?
                                    `${t('dueOn')}: ${formatDate(invoice.due_at)}` :
                                    `${t('overdueSince')}: ${formatDate(invoice.due_at)}`
                            }
                        </div>
                    </div>
                    
                </div>
                <div style={styles.invoiceHeader}>
                    {/* Colonne gauche : Informations de la facture */}
                    <div style={styles.invoiceInfo}>
                        <div style={styles.invoiceMeta}>
                            <div style={styles.invoiceTypeBadge}>
                                <Building2 size={16} />
                                <span>{t('providerInvoice')}</span>
                            </div>
                        </div>

                        <div style={styles.invoiceNumber}>
                            {invoice.invoice_number}
                        </div>
                        <div style={styles.invoiceDates}>
                            <span style={styles.dateItem}>
                                <Calendar size={14} />
                                {t('invoiceDate')}: {formatDate(invoice.invoice_at)}
                            </span>
                            {invoice.due_at && (
                                <span style={styles.dateItem}>
                                    <Clock size={14} />
                                    {t('dueDate')}: {formatDate(invoice.due_at)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Colonne droite : Boutons + Carte Montant */}
                    <div>
                        {/* Boutons d'action */}


                        {/* Carte Montant */}
                        <div style={styles.amountCard}>
                            <div style={styles.amountLabel}>{t('totalAmount')}</div>
                            <div>
                                <span style={styles.amountValue}>
                                    {parseFloat(invoice.amount).toFixed(2)}
                                </span>
                                <span style={styles.amountCurrency}>
                                    {invoice.currency}
                                </span>
                            </div>
                        </div>

                        <div style={styles.headerActions}>
                            <div style={styles.actionGroup}>
                                <button
                                    style={styles.iconButton}
                                    onClick={() => toast.info(t('sharePlaceholder'))}
                                    title={t('share')}
                                >
                                    <Share2 size={18} />
                                </button>
                            </div>

                            <div style={styles.actionSeparator}></div>

                            <div style={styles.exportGroup}>
                                <button
                                    style={styles.secondaryButton}
                                    onClick={() => setShowExportModal(true)}
                                >
                                    <Download size={16} />
                                    {t('export')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>




                {/* Détails de la facture */}
                {/* <div style={styles.detailsGrid}>
                    <div style={styles.detailCard}>
                        <h3 style={styles.detailTitle}>
                            <Calendar size={18} />
                            {t('invoiceDates')}
                        </h3>
                        <div style={styles.detailGrid}>
                            <div style={styles.detailItem}>
                                <Calendar size={18} style={styles.detailIcon} />
                                <div style={styles.detailContent}>
                                    <div style={styles.detailLabel}>{t('invoiceDate')}</div>
                                    <div style={styles.detailValue}>{formatDate(invoice.invoice_at)}</div>
                                </div>
                            </div>
                            <div style={styles.detailItem}>
                                <Clock size={18} style={styles.detailIcon} />
                                <div style={styles.detailContent}>
                                    <div style={styles.detailLabel}>{t('dueDate')}</div>
                                    <div style={styles.detailValue}>{formatDate(invoice.due_at)}</div>
                                </div>
                            </div>
                            {invoice.paid_at && (
                                <div style={styles.detailItem}>
                                    <CheckCircle size={18} style={styles.detailIcon} />
                                    <div style={styles.detailContent}>
                                        <div style={styles.detailLabel}>{t('paidDate')}</div>
                                        <div style={styles.detailValue}>{formatDate(invoice.paid_at)}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={styles.detailCard}>
                        <h3 style={styles.detailTitle}>
                            <CreditCard size={18} />
                            {t('financialDetails')}
                        </h3>
                        <div style={styles.detailGrid}>
                            <div style={styles.detailItem}>
                                <DollarSign size={18} style={styles.detailIcon} />
                                <div style={styles.detailContent}>
                                    <div style={styles.detailLabel}>{t('amount')}</div>
                                    <div style={styles.detailValue}>
                                        {parseFloat(invoice.amount).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                            <div style={styles.detailItem}>
                                <Globe size={18} style={styles.detailIcon} />
                                <div style={styles.detailContent}>
                                    <div style={styles.detailLabel}>{t('currency')}</div>
                                    <div style={styles.detailValue}>{invoice.currency}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>

            {/* Section Émetteur (Fournisseur) */}
            <div style={styles.sectionCard}>
                <div
                    style={styles.sectionHeader}
                    onClick={() => toggleSection('issuer')}
                >
                    <h2 style={styles.sectionTitle}>
                        <Building2 size={20} color="#7c3aed" />
                        {t('provider')}
                        {invoice.issuer?.company && (
                            <span style={styles.sectionBadge}>
                                {invoice.issuer.company}
                            </span>
                        )}
                    </h2>
                    <div style={styles.sectionToggle}>
                        {expandedSections.issuer ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                </div>

                {expandedSections.issuer && invoice.issuer && (
                    <div style={styles.sectionContent}>
                        <div style={{ ...styles.addressCard, background: '#faf5ff', }}>
                            <div style={styles.addressHeader}>
                                <div style={styles.addressIcon}>
                                    <Building2 size={20} />
                                </div>
                                <div>
                                    <h3 style={styles.addressTitle}>{invoice.issuer.name}</h3>
                                    {invoice.issuer.company && (
                                        <p style={styles.addressSubtitle}>{invoice.issuer.company}</p>
                                    )}
                                </div>
                            </div>

                            <div style={styles.addressContent}>
                                <div>
                                    <div style={styles.addressLine}>
                                        <strong>{t('address')}:</strong>
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.issuer.address}
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.issuer.zipCode} {invoice.issuer.city}
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.issuer.country}
                                    </div>
                                </div>

                                <div style={styles.contactInfo}>
                                    <div style={styles.contactItem}>
                                        <Mail size={14} color="#6b7280" />
                                        <a
                                            href={`mailto:${invoice.issuer.email}`}
                                            style={styles.contactLink}
                                        >
                                            {invoice.issuer.email}
                                        </a>
                                    </div>
                                    <div style={styles.contactItem}>
                                        <Phone size={14} color="#6b7280" />
                                        <a
                                            href={`tel:${invoice.issuer.phone}`}
                                            style={styles.contactLink}
                                        >
                                            {invoice.issuer.phone}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Client */}
            <div style={styles.sectionCard}>
                <div
                    style={styles.sectionHeader}
                    onClick={() => toggleSection('customer')}
                >
                    <h2 style={styles.sectionTitle}>
                        <User size={20} color="#7c3aed" />
                        {t('customer')}
                        {invoice.customer?.company && (
                            <span style={styles.sectionBadge}>
                                {invoice.customer.company}
                            </span>
                        )}
                    </h2>
                    <div style={styles.sectionToggle}>
                        {expandedSections.customer ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                </div>

                {expandedSections.customer && invoice.customer && (
                    <div style={styles.sectionContent}>
                        <div style={{ ...styles.addressCard, background: '#f0fdf4', }}>
                            <div style={styles.addressHeader}>
                                <div style={styles.addressIcon}>
                                    <User size={20} />
                                </div>
                                <div>
                                    <h3 style={styles.addressTitle}>{invoice.customer.name}</h3>
                                    {invoice.customer.company && (
                                        <p style={styles.addressSubtitle}>{invoice.customer.company}</p>
                                    )}
                                </div>
                            </div>

                            <div style={styles.addressContent}>
                                <div>
                                    <div style={styles.addressLine}>
                                        <strong>{t('address')}:</strong>
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.customer.address}
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.customer.zipCode} {invoice.customer.city}
                                    </div>
                                    <div style={styles.addressLine}>
                                        {invoice.customer.country}
                                    </div>
                                </div>

                                <div style={styles.contactInfo}>
                                    <div style={styles.contactItem}>
                                        <Mail size={14} color="#6b7280" />
                                        <a
                                            href={`mailto:${invoice.customer.email}`}
                                            style={styles.contactLink}
                                        >
                                            {invoice.customer.email}
                                        </a>
                                    </div>
                                    <div style={styles.contactItem}>
                                        <Phone size={14} color="#6b7280" />
                                        <a
                                            href={`tel:${invoice.customer.phone}`}
                                            style={styles.contactLink}
                                        >
                                            {invoice.customer.phone}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Réservations */}
            <div style={styles.sectionCard}>
                <div
                    style={styles.sectionHeader}
                    onClick={() => toggleSection('bookings')}
                >
                    <h2 style={styles.sectionTitle}>
                        <Calendar size={20} color="#7c3aed" />
                        {t('bookings')}
                        <span style={styles.sectionBadge}>
                            {invoice.bookings.length}
                        </span>
                    </h2>
                    <div style={styles.sectionToggle}>
                        {expandedSections.bookings ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                </div>

                {expandedSections.bookings && invoice.bookings.length > 0 && (
                    <div style={styles.sectionContent}>
                        <div style={styles.bookingsHeader}>
                            <div style={styles.bookingsStats}>
                                <span style={styles.statBadge}>
                                    <FileText size={14} />
                                    {t('totalBookings')}: {invoice.bookings.length}
                                </span>
                                <span style={styles.statBadge}>
                                    <DollarSign size={14} />
                                    {t('totalAmount')}: {invoice.bookings.reduce((sum, b) => sum + parseFloat(b.amount), 0).toFixed(2)} {invoice.currency}
                                </span>
                            </div>
                            <button
                                style={styles.secondaryButton}
                                onClick={() => exportBookingsToExcel(invoice.bookings)}
                            >
                                <Download size={14} />
                                {t('exportBookings')}
                            </button>
                        </div>

                        <div style={styles.bookingsContainer}>
                            <ReusableTable
                                data={invoice.bookings}
                                columns={bookingColumns}
                                onRowClick={(booking) => {
                                    navigate(`/bookings/${booking.booking_number}`);
                                }}
                                onDetails={(booking) => {
                                    navigate(`/bookings/${booking.booking_number}`);
                                }}
                                lang={lang}
                                itemsPerPageOptions={[10, 20, 50, 100]}
                                colors={{
                                    headerBg: '#7c3aed',
                                    buttonDetails: '#10b981'
                                }}
                            />
                        </div>
                    </div>
                )}

                {expandedSections.bookings && invoice.bookings.length === 0 && (
                    <div style={styles.sectionContent}>
                        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <Calendar size={48} color="#d1d5db" />
                            <p style={{ marginTop: '20px', fontSize: '18px', color: '#1f2937' }}>
                                {t('noBookings')}
                            </p>
                            <p style={{ marginTop: '8px', color: '#6b7280' }}>
                                {t('noBookingsDescription')}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Toast notifications */}
            <ToastContainer position="bottom-right" />

            {showExportModal && invoice && (
<InvoiceExport
  isOpen={showExportModal}
  onClose={() => setShowExportModal(false)}
  invoice={invoice}
  formatDate={formatDate}
  getServiceIcon={getServiceIcon}
  lang={lang}
/>
                )}
        </div >
    );
};

export default InvoiceDetailsProvider;